import React, { useEffect, useState } from 'react';
import { testimonials } from '../../mock';
import { Quote, ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { listPosts, wpImage, wpDate, wpReadTime, wpExcerpt } from '../../lib/wp';

export function Testimonials() {
  return (
    <section className="bg-[#f6f1e7] py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-px w-12 bg-[#8a6b3a]" />
          <span className="text-[#8a6b3a] text-xs uppercase tracking-[0.3em]">Voices of Home</span>
        </div>
        <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] text-[#0f1a14] max-w-3xl">Trusted by families who now call the mountains home.</h2>

        <div className="grid md:grid-cols-3 gap-6 mt-14">
          {testimonials.map((t, i) => (
            <div key={i} className="bg-white p-8 border border-[#0f1a14]/10 hover:border-[#8a6b3a] transition-colors relative">
              <Quote className="h-8 w-8 text-[#c9a56a] mb-4" />
              <p className="text-[#0f1a14]/80 leading-relaxed">&ldquo;{t.text}&rdquo;</p>
              <div className="mt-6 pt-6 border-t border-[#0f1a14]/10">
                <div className="font-serif text-lg text-[#0f1a14]">{t.name}</div>
                <div className="text-xs text-[#8a6b3a] uppercase tracking-widest mt-1">{t.city}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function BlogPreview() {

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    listPosts({ perPage: 3 })
      .then((d) => { if (alive) setPosts(d); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  return (
    <section className="bg-white py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="h-px w-12 bg-[#8a6b3a]" />
              <span className="text-[#8a6b3a] text-xs uppercase tracking-[0.3em]">Journal</span>
            </div>
            <h2 className="font-serif text-4xl md:text-6xl text-[#0f1a14] leading-[1.05]">Notes from the hills.</h2>
          </div>
          <Link to="/blog" className="inline-flex items-center gap-2 text-[#8a6b3a] hover:text-[#0f1a14] transition-colors text-sm tracking-widest uppercase">
            All Articles <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {loading && Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-[4/3] bg-[#0f1a14]/5" />
                <div className="mt-5 h-3 w-32 bg-[#0f1a14]/10" />
                <div className="mt-3 h-6 w-4/5 bg-[#0f1a14]/15" />
                <div className="mt-3 h-3 w-full bg-[#0f1a14]/10" />
              </div>
            ))}
            {!loading && posts.map((b) => {
              const img = wpImage(b);
              return (
                <Link key={b.id} to={`/blog/${b.slug}`} className="group block">
                  <div className="relative aspect-[4/3] overflow-hidden bg-[#f6f1e7]">
                    {img && <img src={img} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />}
                  </div>
                  <div className="mt-5">
                    <div className="text-xs uppercase tracking-widest text-[#8a6b3a]">{wpDate(b)} · {wpReadTime(b)} read</div>
                    <h3
                      className="font-serif text-2xl text-[#0f1a14] mt-3 leading-snug group-hover:text-[#8a6b3a] transition-colors"
                      dangerouslySetInnerHTML={{ __html: b.title?.rendered || '' }}
                    />
                    <p className="text-[#0f1a14]/60 mt-3 text-sm leading-relaxed line-clamp-3">{wpExcerpt(b)}</p>
                  </div>
                </Link>
                );
              })}
        </div>
      </div>
    </section>
  );
}

export function CtaBanner() {
  return (
    <section className="relative py-24 lg:py-32 overflow-hidden">
      <img src="https://images.unsplash.com/photo-1454496522488-7a8e488e8606?w=1920&q=80" alt="" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-[#0f1a14]/80" />
      <div className="relative mx-auto max-w-4xl px-6 text-center">
        <div className="text-[#c9a56a] text-xs uppercase tracking-[0.3em] mb-5">Your Mountain, Your Home</div>
        <h2 className="font-serif text-4xl md:text-6xl text-white leading-[1.05]">Come, let&apos;s find your <em className="text-[#c9a56a] not-italic">forever view</em>.</h2>
        <p className="text-white/70 mt-6 max-w-xl mx-auto">Book a complimentary site visit to The Ewen Park. Stay a night in the hills, on us.</p>
        <div className="flex flex-wrap justify-center gap-4 mt-10">
          <Link to="/contact"><Button className="bg-[#c9a56a] text-[#0f1a14] hover:bg-[#b8945a] rounded-none px-8 py-6 tracking-wider">Schedule a Visit</Button></Link>
          <a href="tel:+918526164444"><Button variant="outline" className="bg-transparent border-white/40 text-white hover:bg-white hover:text-[#0f1a14] rounded-none px-8 py-6 tracking-wider">Call +91 852-616-4444</Button></a>
        </div>
      </div>
    </section>
  );
}
